// src/pages/TicketSystem.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchTickets, fetchTicketById } from '../components/api';
import TicketCreateModal from '../components/TicketCreateModal';
import TicketDetailModal from '../components/TicketDetailModal';
import TopBar from '../components/TopBar';
import CountryPicker from '../components/CountryPicker';
import { useUser } from '../context/UserContext';
import COFeedback from './COFeedback';

const COLUMN_CHAR_LIMIT = 40;

const truncateCellText = (value, limit = COLUMN_CHAR_LIMIT) => {
  const text = String(value ?? '');
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}…`;
};

const TicketSystem = ({ userClaims, isEmbedded = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [prefillTicket, setPrefillTicket] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeTab, setActiveTab] = useState('tickets');
  const PAGE_SIZE = 20;
  const PRIORITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3, Custom: 4 };
  const LEVEL_ORDER = { Central: 0, Subnational: 1, Regional: 2, Local: 3 };
  const allowedCountries = user?.allowedCountries;
  const allowedCountriesIso3 = user?.allowedCountriesIso3; // undefined until user loads
  const allowedCountriesKey = Array.isArray(allowedCountriesIso3) ? allowedCountriesIso3.join(',') : '';
  const multipleCountries = Array.isArray(allowedCountriesIso3)
    ? (allowedCountriesIso3.length !== 1 || allowedCountriesIso3.includes('global'))
    : true;
  const singleCountry = Array.isArray(allowedCountriesIso3) && !multipleCountries && allowedCountriesIso3[0]
    ? allowedCountriesIso3[0]
    : null;
  const [filterCountry, setFilterCountry] = useState(singleCountry ?? 'all');
  const [filterCountryOption, setFilterCountryOption] = useState(
    singleCountry && Array.isArray(allowedCountries)
      ? { value: singleCountry, label: allowedCountries[0], iso3: singleCountry }
      : null
  );

  useEffect(() => {
    const loadTickets = async () => {
      try {
        // Wait until user countries are available to avoid duplicate initial fetch
        if (!Array.isArray(allowedCountriesIso3)) {
          return;
        }
        setLoading(true);
        const batchSize = 500;
        let offset = 0;
        let allTickets = [];
        while (true) {
          const chunk = await fetchTickets(userClaims.role, singleCountry, {
            limit: batchSize,
            offset
          });
          allTickets = allTickets.concat(chunk || []);
          if (!chunk || chunk.length < batchSize) break;
          offset += batchSize;
        }
        // Map API fields to UI-friendly format
        const levelNames = { 1: 'Central', 2: 'Subnational', 3: 'Regional', 4: 'Local' };
        const mapped = allTickets.map(t => ({
          ...t,
          // Fallback to existing properties if already in expected shape
          country: t.country ?? t.country_code ?? '',
          level: t.level ?? levelNames[t.level_id] ?? t.level_id ?? '',
          antigen: t.antigen ?? t.antigen_name ?? '',
          createdBy: t.createdBy ?? t.creator_email ?? '',
          assignedTo: t.assignedTo ?? t.assigned_to_email ?? '',
          lastAction: t.lastAction ?? t.updated_at ?? t.created_at ?? '',
          store: t.store ?? t.store_name ?? '',
          province: t.province ?? '',
          district: t.district ?? '',
          isExternalAssignee: !t.assigned_to_user_id && (t.assigned_to_email ?? t.assignedTo),
        }));
        setTickets(mapped);
        setCurrentPage(1);
      } catch (err) {
        console.error("Error loading tickets:", err);
        setError("Failed to load tickets. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    loadTickets();
  }, [userClaims.role, allowedCountriesKey, singleCountry, allowedCountriesIso3]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterPriority, filterCountry, searchTerm, sortColumn, sortDirection]);

  useEffect(() => {
    if (!location.state?.openCreateModal) return;
    setPrefillTicket(location.state.prefillTicket || null);
    setShowCreateModal(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // Normalize backend response to match UI expectations when a new ticket is created
  const handleTicketCreated = (newTicket) => {
    const levelNames = { 1: 'Central', 2: 'Subnational', 3: 'Regional', 4: 'Local' };

    const mappedTicket = {
      ...newTicket,
      country: newTicket.country ?? newTicket.country_code ?? '',
      level: newTicket.level ?? levelNames[newTicket.level_id] ?? newTicket.level_id ?? '',
      antigen: newTicket.antigen ?? newTicket.antigen_name ?? '',
      createdBy: newTicket.createdBy ?? newTicket.creator_email ?? '',
      assignedTo: newTicket.assignedTo ?? newTicket.assigned_to_email ?? '',
      lastAction: newTicket.lastAction ?? newTicket.updated_at ?? newTicket.created_at ?? '',
      store: newTicket.store ?? newTicket.store_name ?? '',
      province: newTicket.province ?? '',
      district: newTicket.district ?? '',
      isExternalAssignee: !newTicket.assigned_to_user_id && (newTicket.assigned_to_email ?? newTicket.assignedTo),
    };

    setTickets(prevTickets => [mappedTicket, ...prevTickets]);
    setShowCreateModal(false);
  };

  const handleTicketUpdated = (updatedTicket) => {
    setTickets(prevTickets => 
      prevTickets.map(ticket => 
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
    setSelectedTicket(null);
  };

  const openTicketDetail = async (ticket) => {
    try {
      const detailed = await fetchTicketById(ticket.id);
      const levelNames = { 1: 'Central', 2: 'Subnational', 3: 'Regional', 4: 'Local' };
      const normalized = {
        ...ticket,
        ...detailed,
        level: detailed.level ?? levelNames[detailed.level_id] ?? ticket.level ?? '',
      };
      setSelectedTicket(normalized);
    } catch (e) {
      // Fallback to the list item if detail fetch fails
      setSelectedTicket(ticket);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    // Filter by status
    if (filterStatus !== 'all' && ticket.status !== filterStatus) {
      return false;
    }
    
    // Filter by priority
    if (filterPriority !== 'all' && ticket.priority !== filterPriority) {
      return false;
    }
    // Filter by country
    if (filterCountry !== 'all' && ticket.country !== filterCountry) {
      return false;
    }
    
    // Search by title, ID, or store
    const lowerSearch = searchTerm.toLowerCase();
    if (
      searchTerm &&
      !ticket.title.toLowerCase().includes(lowerSearch) &&
      !(ticket.id && ticket.id.toString().includes(lowerSearch)) &&
      !(ticket.store && ticket.store.toLowerCase().includes(lowerSearch))
    ) {
      return false;
    }
    
    return true;
  });

  const getSortValue = (ticket, column) => {
    switch (column) {
      case 'title':
        return ticket.title || '';
      case 'store':
        return ticket.store || '';
      case 'level':
        if (LEVEL_ORDER[ticket.level] !== undefined) return LEVEL_ORDER[ticket.level];
        if (ticket.level_id != null && ticket.level_id !== '') return Number(ticket.level_id);
        return ticket.level || '';
      case 'antigen':
        return ticket.antigen || '';
      case 'createdBy':
        return ticket.createdBy || '';
      case 'lastAction':
        return ticket.lastAction ? new Date(ticket.lastAction).getTime() : 0;
      case 'status':
        return ticket.status || '';
      case 'priority':
        return PRIORITY_ORDER[ticket.priority] !== undefined
          ? PRIORITY_ORDER[ticket.priority]
          : (ticket.priority || '');
      default:
        return '';
    }
  };

  const compareSortValues = (a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
  };

  const sortedTickets = !sortColumn
    ? filteredTickets
    : [...filteredTickets].sort((a, b) => {
        const result = compareSortValues(getSortValue(a, sortColumn), getSortValue(b, sortColumn));
        return sortDirection === 'asc' ? result : -result;
      });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const renderSortableHeader = (label, column, className = '') => {
    const isActive = sortColumn === column;
    const indicator = !isActive ? '↕' : sortDirection === 'asc' ? '↑' : '↓';
    return (
      <th
        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b cursor-pointer select-none hover:bg-gray-200 ${className}`.trim()}
        onClick={() => handleSort(column)}
        aria-sort={isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <span className={`text-[10px] ${isActive ? 'text-gray-700' : 'text-gray-400'}`}>{indicator}</span>
        </span>
      </th>
    );
  };

  const totalPages = Math.max(1, Math.ceil(sortedTickets.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = (safeCurrentPage - 1) * PAGE_SIZE;
  const paginatedTickets = sortedTickets.slice(pageStart, pageStart + PAGE_SIZE);

  const renderPaginationControls = (className = '') => (
    <div className={`flex items-center justify-between ${className}`.trim()}>
      <div className="text-sm text-gray-600">
        Showing {filteredTickets.length === 0 ? 0 : pageStart + 1}-
        {Math.min(pageStart + PAGE_SIZE, filteredTickets.length)} of {filteredTickets.length}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
          disabled={safeCurrentPage <= 1}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {safeCurrentPage} / {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
          disabled={safeCurrentPage >= totalPages}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );

  // Get status color based on ticket status
  const getStatusColor = (status) => {
    switch(status) {
      case 'Open':
        return 'bg-red-100 text-red-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Resolved':
        return 'bg-green-100 text-green-800';
      case 'Canceled':
        return 'bg-red-100 text-red-800';
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get priority color based on ticket priority
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'Critical':
        return 'bg-red-100 text-red-800';
      case 'High':
        return 'bg-orange-100 text-orange-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get level color based on ticket level
  const getLevelColor = (level) => {
    switch(level) {
      case 'Central':
        return 'bg-indigo-100 text-indigo-800';
      case 'Subnational':
        return 'bg-teal-100 text-teal-800';
      case 'Regional':
        return 'bg-purple-100 text-purple-800';
      case 'Local':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const content = (
    <div className="p-4">
      <div className="bg-white shadow-md rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Ticket Management System</h2>
        
        <div className="flex gap-4">
          {/* Create button - available to all users */}
          {activeTab === 'tickets' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
            >
              Create New Ticket
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Ticket system tabs">
          <button
            type="button"
            onClick={() => setActiveTab('tickets')}
            className={`border-b-2 px-1 py-3 text-sm font-medium ${
              activeTab === 'tickets'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            Tickets
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('co-feedback')}
            className={`border-b-2 px-1 py-3 text-sm font-medium ${
              activeTab === 'co-feedback'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            CO Feedback
          </button>
        </nav>
      </div>

      {activeTab === 'tickets' && (
        <>
      
      {/* Filters */}
      <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search by title, ID, or store..."
              className="w-full p-2 border rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {multipleCountries && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <CountryPicker
                value={filterCountryOption}
                onChange={(opt) => {
                  setFilterCountryOption(opt);
                  const countryValue = opt?.iso3 || opt?.value || 'all';
                  setFilterCountry(countryValue);
                }}
                placeholder="All Countries"
                isClearable={true}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="p-2 border rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Canceled">Canceled</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="p-2 border rounded-md"
            >
              <option value="all">All Priorities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Loading and error states */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      
      {/* Tickets table */}
      {!loading && !error && (
        <>
          {filteredTickets.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No tickets found. {userClaims.role === 'Country Office' && 'Create a new ticket to get started.'}</p>
            </div>
          ) : (
            <>
            {renderPaginationControls('mb-4')}
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    {renderSortableHeader('Title / ID', 'title', 'w-72')}
                    {renderSortableHeader('Store', 'store')}
                    {renderSortableHeader('SC Level', 'level')}
                    {renderSortableHeader('Antigen', 'antigen')}
                    {renderSortableHeader('Created / Assigned', 'createdBy')}
                    {renderSortableHeader('Last Action', 'lastAction')}
                    {renderSortableHeader('Status', 'status')}
                    {renderSortableHeader('Priority', 'priority')}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedTickets.map((ticket) => (
                    <tr 
                      key={ticket.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openTicketDetail(ticket)}
                    >
                      <td className="px-6 py-4 max-w-xs text-sm text-gray-900" title={ticket.title}>
                        <div className="truncate">{truncateCellText(ticket.title)}</div>
                        {ticket.id && (
                          <span className="block truncate text-xs text-gray-500">#{ticket.id}</span>
                        )}
                      </td>
                      <td
                        className="px-6 py-4 max-w-xs text-sm"
                        title={[ticket.store, ticket.country, [ticket.province, ticket.district].filter(Boolean).join(', ')].filter(Boolean).join('\n')}
                      >
                        <div className="truncate text-gray-900">{truncateCellText(ticket.store)}</div>
                        {ticket.country && (
                          <div className="truncate text-xs text-gray-500">{truncateCellText(ticket.country)}</div>
                        )}
                        {(ticket.province || ticket.district) && (
                          <div className="truncate text-xs text-gray-500">
                            {truncateCellText(`${ticket.province || ''}${ticket.province && ticket.district ? ', ' : ''}${ticket.district || ''}`)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 max-w-[8rem] whitespace-nowrap" title={ticket.level}>
                        <span className={`px-2 inline-flex max-w-full truncate text-xs leading-5 font-semibold rounded-full ${getLevelColor(ticket.level)}`}>
                          {truncateCellText(ticket.level)}
                        </span>
                      </td>
                      <td className="px-6 py-4 max-w-xs truncate text-sm text-gray-500" title={ticket.antigen}>
                        {(() => {
                          const parts = (ticket.antigen || '').split(',').map(p => p.trim()).filter(Boolean);
                          const preview = parts.slice(0, 3).join(', ');
                          const text = parts.length > 3 ? `${preview}…` : preview;
                          return truncateCellText(text);
                        })()}
                      </td>
                      <td
                        className="px-6 py-4 max-w-xs text-sm"
                        title={[ticket.createdBy, ticket.assignedTo].filter(Boolean).join('\n')}
                      >
                        <div className="truncate text-gray-900">{truncateCellText(ticket.createdBy)}</div>
                        {ticket.assignedTo && (
                          <div className="mt-1 truncate text-gray-500">
                            {(() => {
                              const emails = String(ticket.assignedTo).split(',').map((email) => email.trim()).filter(Boolean);
                              const preview = emails.slice(0, 2).join(', ');
                              const text = emails.length > 2 ? `${preview}…` : preview;
                              return truncateCellText(text);
                            })()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 max-w-[8rem] truncate whitespace-nowrap text-sm text-gray-500" title={ticket.lastAction || ''}>
                        {ticket.lastAction ? new Date(ticket.lastAction).toLocaleDateString() : ''}
                      </td>
                      <td className="px-6 py-4 max-w-[8rem] whitespace-nowrap" title={ticket.status}>
                        <span className={`px-2 inline-flex max-w-full truncate text-xs leading-5 font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                          {truncateCellText(ticket.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 max-w-[8rem] whitespace-nowrap" title={ticket.priority}>
                        <span className={`px-2 inline-flex max-w-full truncate text-xs leading-5 font-semibold rounded-full ${getPriorityColor(ticket.priority)}`}>
                          {truncateCellText(ticket.priority)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderPaginationControls('mt-4')}
            </>
          )}
        </>
      )}
      
      {/* Ticket create modal */}
      <TicketCreateModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setPrefillTicket(null);
        }}
        onTicketCreated={handleTicketCreated}
        userRole={userClaims.role}
        userEmail={userClaims.email}
        countryCode={userClaims.countryCode}
        prefillData={prefillTicket}
      />
      
      {/* Ticket detail modal */}
      {selectedTicket && (
        <TicketDetailModal
          isOpen={!!selectedTicket}
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onTicketUpdated={handleTicketUpdated}
          userRole={userClaims.role}
          userEmail={userClaims.email}
        />
      )}
        </>
      )}

      {activeTab === 'co-feedback' && (
        <div className="-mx-4 -mb-4">
          <COFeedback userClaims={userClaims} isEmbedded />
        </div>
      )}
      </div>
    </div>
  );

  if (isEmbedded) return content;

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="ticketing-system" userClaims={userClaims} />
      {content}
    </div>
  );
};

export default TicketSystem; 